import React from "react";
import handImage from "../assets/img_hero_hand_ai.png";
import aboutUsImage from "../assets/img_home.png";
import iconInnovation from "../assets/icon_innovation.svg";
import iconEndToEnd from "../assets/icon_endtoend.svg";
import iconImpact from "../assets/icon_impact.svg";
import { useNavigate, Link } from "react-router-dom";
import { useProjects } from "./ProjectContext";
import AppCard from "../components/AppCard";
import Reveal from "../components/Reveal";

const HomePage = () => {
  const navigate = useNavigate();
  const { projects } = useProjects();
  const featured = projects.slice(0, 3);

  return (
    <main className="bg-ink">
      {/* ================= HERO ================= */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-32 pb-20 overflow-hidden">
        {/* brand glow + masked grid, the Nudge hero backdrop */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_55%,rgba(91,62,255,0.16)_0%,transparent_70%),radial-gradient(ellipse_40%_35%_at_18%_80%,rgba(25,208,224,0.08)_0%,transparent_60%)]" />
        <div className="pointer-events-none absolute inset-0 hero-grid" />

        <div className="relative z-10 w-full max-w-5xl mx-auto">
          <p className="eyebrow fade-up mb-8" style={{ animationDelay: "0.2s" }}>
            UK Product Studio
          </p>

          <h1
            className="font-display text-hero text-paper tracking-wide fade-up"
            style={{ animationDelay: "0.4s" }}
          >
            We build <span className="text-brand-cyan">AI</span>
          </h1>
          <h1
            className="font-display text-hero text-outline tracking-wide mb-10 fade-up"
            style={{ animationDelay: "0.5s" }}
          >
            that ships.
          </h1>

          <p
            className="text-[17px] sm:text-lg text-muted max-w-xl mx-auto leading-relaxed mb-12 fade-up"
            style={{ animationDelay: "0.7s" }}
          >
            A UK studio building AI-powered products for{" "}
            <strong className="text-paper font-medium">
              healthcare, science and the creative arts
            </strong>{" "}
            — our own apps, and our clients'.
          </p>

          <div
            className="flex flex-wrap gap-4 justify-center fade-up"
            style={{ animationDelay: "0.9s" }}
          >
            <button
              className="btn-primary"
              onClick={() => navigate("/portfolio")}
            >
              See our work
            </button>
            <button className="btn-ghost" onClick={() => navigate("/services")}>
              What we do
            </button>
          </div>

          {/* hero phone */}
          <div
            className="mt-20 flex justify-center fade-up"
            style={{ animationDelay: "1.1s" }}
          >
            <img
              src={handImage}
              alt="An AI product running on a phone"
              className="w-full max-w-3xl h-auto object-contain drop-shadow-[0_40px_80px_rgba(0,0,0,0.8)]"
            />
          </div>
        </div>
      </section>

      {/* ================= CAPABILITY STRIP ================= */}
      <section className="border-y border-ink-line bg-ink-soft">
        <div className="max-w-5xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-14 text-center">
          {[
            {
              icon: iconInnovation,
              title: "Innovation Redesigned",
              text: "Tailored solutions that propel your business forward.",
            },
            {
              icon: iconEndToEnd,
              title: "End-to-End Excellence",
              text: "From concept to deployment, we handle it all seamlessly.",
            },
            {
              icon: iconImpact,
              title: "Sector Specific Impact",
              text: "Crafted solutions for maximum industry impact.",
            },
          ].map((item, i) => (
            <Reveal key={item.title} delay={i * 120}>
              <img
                src={item.icon}
                alt=""
                aria-hidden="true"
                className="w-12 h-12 mb-6 object-contain mx-auto brightness-0 invert opacity-80"
              />
              <h3 className="font-display text-[24px] tracking-wide text-paper mb-3">
                {item.title}
              </h3>
              <p className="text-sm text-muted leading-relaxed max-w-xs mx-auto">
                {item.text}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ================= SHIPPED WORK ================= */}
      <section className="px-6 py-28">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-16">
            <p className="eyebrow mb-5">Proof, not promises</p>
            <h2 className="display-title max-w-2xl">
              Apps we've shipped
            </h2>
            <p className="text-body text-muted max-w-md mt-6">
              Products we designed, built, launched — and still maintain.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 110}>
                <AppCard project={p} />
              </Reveal>
            ))}
          </div>

          <Reveal className="text-center mt-16">
            <Link to="/portfolio" className="btn-primary">
              View all work
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="border-t border-ink-line bg-ink-soft px-6 py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">
          <Reveal>
            <img
              src={aboutUsImage}
              alt="The Deep-C Vision team at work"
              className="rounded-tile w-full h-auto object-cover border border-ink-line shadow-card"
            />
          </Reveal>

          <Reveal delay={120}>
            <p className="eyebrow mb-5">Fusing design and technology</p>
            <h2 className="display-title mb-6">About us</h2>
            <p className="text-body text-muted mb-8">
              Unlock the power of innovation with Deep-C Vision, your go-to
              partner for intelligent software solutions. Specialising in mobile
              apps for creative arts, science outreach, and healthcare, we
              redefine industry standards. Our commitment to innovation,
              comprehensive services, and specialised expertise ensures your
              business stands out.
            </p>
            <Link to="/aboutus" className="btn-primary">
              Our story
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
